import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, UsersRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MultiSelectPopover } from '@/shared/components/MultiSelectPopover';
import type {
  TrainingDirectoryEmployee,
  TrainingProfile,
  TrainingProfileInput,
  TrainingProfileQuestion,
} from '../../types/training.types';

function nextId(prefix: string) {
  return `${prefix}-${crypto.randomUUID()}`;
}

function newQuestion(): TrainingProfileQuestion {
  const questionId = nextId('question');
  const firstOptionId = nextId('option');
  return {
    id: questionId,
    prompt: '',
    options: [
      { id: firstOptionId, label: '' },
      { id: nextId('option'), label: '' },
    ],
    correctOptionId: firstOptionId,
  };
}

function initialInput(profile?: TrainingProfile | null): TrainingProfileInput {
  if (profile) {
    return {
      name: profile.name,
      documentGroup: profile.documentGroup,
      gracePeriodDays: profile.gracePeriodDays,
      quizRequired: profile.quizRequired,
      passingScore: profile.passingScore,
      maxAttempts: profile.maxAttempts,
      questions: profile.questions,
      active: profile.active,
      members: profile.members.map((member) => ({
        userId: member.userId,
        supervisorUserId: member.supervisorUserId,
      })),
    };
  }
  return {
    name: '',
    documentGroup: '',
    gracePeriodDays: 14,
    quizRequired: true,
    passingScore: 80,
    maxAttempts: 3,
    questions: [newQuestion()],
    active: true,
    members: [],
  };
}

interface TrainingProfileDialogProps {
  open: boolean;
  profile?: TrainingProfile | null;
  directory: TrainingDirectoryEmployee[];
  isPending: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (input: TrainingProfileInput) => Promise<void>;
}

export function TrainingProfileDialog({
  open,
  profile,
  directory,
  isPending,
  onOpenChange,
  onSave,
}: TrainingProfileDialogProps) {
  const [input, setInput] = useState<TrainingProfileInput>(() => initialInput(profile));

  useEffect(() => {
    if (open) setInput(initialInput(profile));
  }, [open, profile]);

  const directoryById = useMemo(
    () => new Map(directory.map((employee) => [employee.id, employee])),
    [directory],
  );
  const selectedMemberIds = input.members.map((member) => member.userId);
  const canSave = input.name.trim().length >= 3
    && input.documentGroup.trim().length >= 2
    && input.members.length > 0
    && (!input.quizRequired || input.questions.every((question) =>
      question.prompt.trim().length >= 3
      && question.options.length >= 2
      && question.options.every((option) => option.label.trim().length > 0)
      && question.options.some((option) => option.id === question.correctOptionId)));

  const setMemberIds = (ids: string[]) => {
    setInput((current) => ({
      ...current,
      members: ids.map((userId) => ({
        userId,
        supervisorUserId: current.members.find((member) => member.userId === userId)?.supervisorUserId
          ?? directoryById.get(userId)?.managerUserId
          ?? null,
      })),
    }));
  };

  const updateQuestion = (
    questionId: string,
    update: (question: TrainingProfileQuestion) => TrainingProfileQuestion,
  ) => {
    setInput((current) => ({
      ...current,
      questions: current.questions.map((question) =>
        question.id === questionId ? update(question) : question),
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 text-teal-700">
            <UsersRound className="h-5 w-5" />
          </div>
          <DialogTitle>{profile ? 'Edit training matrix' : 'Create training matrix'}</DialogTitle>
          <DialogDescription>
            Every future Effective release in this document group will create governed assignments
            for these employees. Existing assignment evidence is never rewritten.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="training-profile-name">Profile name</Label>
              <Input
                id="training-profile-name"
                value={input.name}
                onChange={(event) => setInput((current) => ({ ...current, name: event.target.value }))}
                placeholder="Production SOP qualification"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="training-document-group">Document group</Label>
              <Input
                id="training-document-group"
                value={input.documentGroup}
                onChange={(event) => setInput((current) => ({ ...current, documentGroup: event.target.value }))}
                placeholder="Production"
              />
              <p className="text-xs text-slate-500">Must match the controlled document department.</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Assigned employees</Label>
            <MultiSelectPopover
              value={selectedMemberIds}
              options={directory.map((employee) => ({
                id: employee.id,
                label: employee.name,
                secondaryLabel: [employee.position, employee.email].filter(Boolean).join(' · '),
              }))}
              onChange={setMemberIds}
              placeholder="Select employees"
              searchPlaceholder="Search employees…"
              showSelectAll
            />
            <p className="text-xs text-slate-500">
              Supervisor escalation follows each employee’s manager unless an existing matrix override is retained.
            </p>
          </div>

          <div className="grid gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="training-grace-days">Due in days</Label>
              <Input
                id="training-grace-days"
                type="number"
                min={1}
                max={365}
                value={input.gracePeriodDays}
                onChange={(event) => setInput((current) => ({
                  ...current,
                  gracePeriodDays: Number(event.target.value),
                }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="training-passing-score">Passing score</Label>
              <Input
                id="training-passing-score"
                type="number"
                min={1}
                max={100}
                disabled={!input.quizRequired}
                value={input.passingScore}
                onChange={(event) => setInput((current) => ({
                  ...current,
                  passingScore: Number(event.target.value),
                }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="training-max-attempts">Maximum attempts</Label>
              <Input
                id="training-max-attempts"
                type="number"
                min={1}
                max={10}
                disabled={!input.quizRequired}
                value={input.maxAttempts}
                onChange={(event) => setInput((current) => ({
                  ...current,
                  maxAttempts: Number(event.target.value),
                }))}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-6 rounded-xl border border-slate-200 px-4 py-3">
            <label className="flex items-center gap-2 text-sm font-medium">
              <Checkbox
                checked={input.quizRequired}
                onCheckedChange={(checked) => setInput((current) => ({
                  ...current,
                  quizRequired: checked === true,
                  questions: checked === true && current.questions.length === 0
                    ? [newQuestion()]
                    : current.questions,
                }))}
              />
              Require assessment
            </label>
            <label className="flex items-center gap-2 text-sm font-medium">
              <Checkbox
                checked={input.active}
                onCheckedChange={(checked) => setInput((current) => ({
                  ...current,
                  active: checked === true,
                }))}
              />
              Matrix active
            </label>
          </div>

          {input.quizRequired && (
            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-slate-950">Assessment questions</h3>
                  <p className="text-xs text-slate-500">Correct answers are never sent to the employee portal.</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setInput((current) => ({
                    ...current,
                    questions: [...current.questions, newQuestion()],
                  }))}
                >
                  <Plus className="mr-1 h-4 w-4" /> Add question
                </Button>
              </div>

              {input.questions.map((question, questionIndex) => (
                <div key={question.id} className="space-y-3 rounded-xl border border-slate-200 p-4">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 space-y-2">
                      <Label htmlFor={`training-question-${question.id}`}>
                        Question {questionIndex + 1}
                      </Label>
                      <Input
                        id={`training-question-${question.id}`}
                        value={question.prompt}
                        onChange={(event) => updateQuestion(question.id, (current) => ({
                          ...current,
                          prompt: event.target.value,
                        }))}
                        placeholder="What must be verified before this procedure begins?"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="mt-6 text-slate-400 hover:text-rose-700"
                      disabled={input.questions.length === 1}
                      onClick={() => setInput((current) => ({
                        ...current,
                        questions: current.questions.filter((candidate) => candidate.id !== question.id),
                      }))}
                      aria-label={`Remove question ${questionIndex + 1}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    {question.options.map((option, optionIndex) => (
                      <div key={option.id} className="space-y-1">
                        <Label htmlFor={`training-option-${option.id}`}>Option {optionIndex + 1}</Label>
                        <Input
                          id={`training-option-${option.id}`}
                          value={option.label}
                          onChange={(event) => updateQuestion(question.id, (current) => ({
                            ...current,
                            options: current.options.map((candidate) =>
                              candidate.id === option.id
                                ? { ...candidate, label: event.target.value }
                                : candidate),
                          }))}
                          placeholder="Answer option"
                        />
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap items-end justify-between gap-3">
                    <div className="w-full space-y-2 sm:w-64">
                      <Label>Correct answer</Label>
                      <Select
                        value={question.correctOptionId}
                        onValueChange={(value) => updateQuestion(question.id, (current) => ({
                          ...current,
                          correctOptionId: value,
                        }))}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {question.options.map((option, optionIndex) => (
                            <SelectItem key={option.id} value={option.id}>
                              Option {optionIndex + 1}{option.label ? ` · ${option.label}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => updateQuestion(question.id, (current) => ({
                        ...current,
                        options: [...current.options, { id: nextId('option'), label: '' }],
                      }))}
                      disabled={question.options.length >= 8}
                    >
                      <Plus className="mr-1 h-4 w-4" /> Add option
                    </Button>
                  </div>
                </div>
              ))}
            </section>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button
            className="bg-teal-700 text-white hover:bg-teal-800"
            disabled={!canSave || isPending}
            onClick={() => onSave({
              ...input,
              name: input.name.trim(),
              documentGroup: input.documentGroup.trim(),
              questions: input.quizRequired ? input.questions : [],
            })}
          >
            {isPending ? 'Saving…' : profile ? 'Save matrix' : 'Create matrix'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
